import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Settings, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import IconTooltip from '@/components/IconTooltip';
import StudyToolsPopover from './StudyToolsPopover';

/**
 * Chat toolbar: New Chat, Tools popover | Delete, Settings
 */
export default function ChatToolbar({
  currentConversation,
  userSettings,
  isGuest,
  isPaid,
  fileInputRef,
  fileUploading,
  youtubeUrl,
  setYoutubeUrl,
  youtubeAnalyzing,
  hasFeature,
  onCreateNewConversation,
  onFileUpload,
  onAnalyzeYouTube,
  onToggleStudyTool,
  onDeleteConversation,
  onToggleSettings,
  onShowUpgrade,
}) {
  const { t } = useTranslation();

  return (
    <div className="flex gap-2 mb-2 px-2">
      <div className="flex justify-between items-center w-full">
        {/* Left group */}
        <div className="flex items-center gap-1.5">
          <IconTooltip label={t('header.new_chat_tooltip', 'New Chat')}>
            <Button variant="ghost" size="sm" onClick={() => onCreateNewConversation()}
              className="rounded-full hover:bg-[var(--theme-hover)] theme-text-secondary hover:text-[var(--theme-accent)] transition-colors h-10 w-10 p-0"
              data-testid="new-chat-input-button"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </IconTooltip>

          {/* accept=" ... " uses BOTH proper MIME types AND the file
              extension fallback. Android WebViews (including the Despia
              wrapper) often refuse to show documents when the picker is
              filtered by extension alone — the standard picker maps
              extensions via MIME databases that are patchy on many
              devices. iOS Safari on the other hand relies on the
              extension list. Including both keeps every platform happy.

              Position the input off-screen instead of `display: none`.
              Some older Android WebViews silently ignore `.click()` on
              display:none file inputs, so we keep it in the layout but
              visually and pointer-invisible. This is the same pattern
              used by React-Dropzone and other established libraries. */}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.txt,.docx"
            onChange={onFileUpload}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
              opacity: 0,
              pointerEvents: 'none',
            }}
            tabIndex={-1}
            aria-hidden="true"
            data-testid="chat-file-input"
          />

          {/* Tools Popover — consolidates study tools + YouTube + file upload */}
          <StudyToolsPopover
            userSettings={userSettings}
            isPaid={isPaid}
            onToggle={onToggleStudyTool}
            onShowUpgrade={onShowUpgrade}
            fileInputRef={fileInputRef}
            fileUploading={fileUploading}
            onFileUpload={onFileUpload}
            youtubeUrl={youtubeUrl}
            setYoutubeUrl={setYoutubeUrl}
            youtubeAnalyzing={youtubeAnalyzing}
            onAnalyzeYouTube={onAnalyzeYouTube}
          />
        </div>

        {/* Right group */}
        <div className="flex gap-1.5">
          <AlertDialog>
            <IconTooltip label={t('chat.delete_conv_tooltip', 'Delete Conversation')}>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="delete-conversation-button"
                  className="rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors h-10 w-10 p-0"
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </AlertDialogTrigger>
            </IconTooltip>
            <AlertDialogContent className="rounded-[2.5rem] bg-card border border-border shadow-2xl p-8">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-foreground text-xl font-bold">{t('chat.delete_conv_title')}</AlertDialogTitle>
                <AlertDialogDescription className="text-muted-foreground text-base mt-2">
                  {t('chat.delete_conv_desc')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="mt-6 gap-3">
                <AlertDialogCancel className="rounded-2xl border-border text-foreground hover:bg-muted h-12 px-6">{t('chat.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDeleteConversation(currentConversation?.conversation_id)}
                  className="rounded-2xl bg-destructive hover:bg-destructive/90 text-destructive-foreground font-medium shadow-md border-0 h-12 px-6"
                >
                  {t('chat.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <IconTooltip label={t('chat.settings_tooltip', 'Settings')}>
            <Button variant="ghost" size="sm" onClick={onToggleSettings}
              className="rounded-full hover:bg-[var(--theme-hover)] theme-text-secondary hover:text-[var(--theme-accent)] transition-colors h-10 w-10 p-0"
              data-testid="quick-settings-button"
            >
              <Settings className="h-5 w-5" />
            </Button>
          </IconTooltip>
        </div>
      </div>
    </div>
  );
}
